import React, { useContext } from "react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { SearchResultPlan, DrugCostInfo, PharmacyType } from "../../@types";
import {
  useTranslate,
  ifNumber,
  isPdPlanType,
  isMapdPlanType,
  shouldSuppressDrugPricing,
} from "../../helpers";
import Tooltip from "../Tooltip";
import { AppContext } from "../../app/store";

interface YearlyCostProps {
  plan: SearchResultPlan;
  drugCostInfo: DrugCostInfo;
}

const YearlyCost: React.FunctionComponent<YearlyCostProps> = ({
  plan,
  drugCostInfo,
}) => {
  const t = useTranslate();
  const {
    state: { pharmacyType, prescriptions },
  } = useContext(AppContext);
  const {
    mctYpOutsideOpenEnrollment,
    mctYpOeNextYear,
    mctCurrentPlanYear,
  } = useFlags();

  const isPDP = isPdPlanType(plan.plan_type);
  const shouldSuppress =
    prescriptions.length && shouldSuppressDrugPricing(plan);

  const premiumToAdd = plan.total_remaining_premium
    ? plan.total_remaining_premium
    : 0;

  const totalMail = plan.remaining_premium_and_drugs
    ? plan.remaining_premium_and_drugs_mail_order
    : typeof drugCostInfo.lowest_mail_total === "string"
    ? drugCostInfo.lowest_mail_total
    : drugCostInfo.lowest_mail_total + premiumToAdd;

  const totalRetail = plan.remaining_premium_and_drugs
    ? plan.remaining_premium_and_drugs_retail
    : typeof drugCostInfo.lowest_retail_total === "string"
    ? drugCostInfo.lowest_retail_total
    : drugCostInfo.lowest_retail_total + premiumToAdd;

  const mapdOnlyText = isMapdPlanType(plan.plan_type)
    ? t("plan_card.does_not_include_health_costs")
    : null;

  const mailSection = (
    <div className="PlanCard__info_group">
      {plan.does_not_support_mail_order ||
      (!plan.remaining_premium_and_drugs &&
        !drugCostInfo.costs.find(c => c.mail_order)) ? (
        <span data-testid="mail-order-not-covered">
          {t("plan_terms.does_not_cover_mail_order")}
        </span>
      ) : (
        <>
          <span className="PlanCard__data e2e-total-mail-cost">
            {shouldSuppress ? t("na") : ifNumber(totalMail)}
          </span>
          <div className="PlanCard__line_items">
            <Tooltip
              triggerText={t("plan_card.mail_total_cost")}
              title={t("plan_card.mail_total_cost")}
            >
              {mctYpOutsideOpenEnrollment
                ? t("plan_card.tooltip.mail_total_cost.OOE")
                : t("plan_card.tooltip.mail_total_cost.OE")}
            </Tooltip>
            {mapdOnlyText}
          </div>
        </>
      )}
    </div>
  );

  const retailSection = (
    <div className="PlanCard__info_group">
      <span className="PlanCard__data e2e-total-retail-cost">
        {shouldSuppress ? t("na") : ifNumber(totalRetail)}
      </span>
      <div className="PlanCard__line_items">
        <Tooltip
          triggerText={t("plan_card.retail_total_cost")}
          title={t("plan_card.retail_total_cost")}
        >
          {mctYpOutsideOpenEnrollment
            ? t("plan_card.tooltip.retail_total_cost.OOE")
            : t("plan_card.tooltip.retail_total_cost.OE")}
        </Tooltip>
        {mapdOnlyText}
      </div>
    </div>
  );

  let noDrugsSection;
  if (!prescriptions.length) {
    noDrugsSection = (
      <div className="PlanCard__info_group">
        <span className="PlanCard__data e2e-total-retail-cost">
          {shouldSuppress ? t("na") : ifNumber(totalRetail)}
        </span>
        <span>
          {!mctYpOeNextYear &&
          mctCurrentPlanYear &&
          plan.contract_year !== mctCurrentPlanYear.toString()
            ? t("plan_card.total_cost_no_drugs.whole_year")
            : t("plan_card.total_cost_no_drugs.remainder_year")}
        </span>
      </div>
    );
  }

  return (
    <div className="PlanCard__section" data-testid="yearlyCost">
      <h3 className="PlanCard__section_heading">
        {t("plan_card.section_heading.yearly_cost")}
      </h3>
      <div>
        {noDrugsSection ? (
          noDrugsSection
        ) : isPDP ? (
          <>
            {pharmacyType !== PharmacyType.MAIL && retailSection}
            {pharmacyType !== PharmacyType.RETAIL && mailSection}
          </>
        ) : pharmacyType === PharmacyType.MAIL ? (
          mailSection
        ) : (
          retailSection
        )}
      </div>
    </div>
  );
};

export default YearlyCost;
